import { apiClient, ApiError } from './apiClient';
import type {
	CreateResourcePostRequestDto,
	CreateResourcePostResponseDto,
	DeleteResourcePostResponseDto,
	GetResourcePostDetailResponseDto,
	GetResourcePostListParams,
	GetResourcePostListResponseDto,
	UpdateResourcePostRequestDto,
	UpdateResourcePostResponseDto,
} from '@/types/api/resource-post';

/** 공개 자료실 조회 시 Bearer 없이 호출 (만료 토큰·비로그인 401 방지) */
export type ResourcePostReadOptions = {
	skipAuth?: boolean;
};

/**
 * 자료실 리스트 조회
 * API 문서: GET /resource-post/list
 */
export async function getResourcePostList(
	params: GetResourcePostListParams,
	options?: ResourcePostReadOptions
): Promise<GetResourcePostListResponseDto> {
	try {
		const response = await apiClient.get<{
			resourcePosts: Array<{
				id: number;
				title: string;
				description: string;
			}>;
			total: number;
		}>(`/resource-post/list`, {
			params,
			skipAuth: options?.skipAuth === true,
		});

		return {
			resourcePosts: response.resourcePosts.map(item => ({
				id: item.id,
				title: item.title,
				description: item.description,
			})),
			total: response.total,
		};
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '자료실 목록을 불러오는데 실패했습니다.');
		}
		throw error;
	}
}

/**
 * 자료 상세 조회
 * API 문서: GET /resource-post/:resourcePostId/detail
 */
export async function getResourcePostDetail(
	resourcePostId: number,
	language?: string,
	options?: ResourcePostReadOptions
): Promise<GetResourcePostDetailResponseDto> {
	try {
		const response = await apiClient.get<{
			id: number;
			title: string;
			description: string;
			category: string;
			content: string | null;
			attachmentUrl: string | null;
		}>(`/resource-post/${resourcePostId}/detail`, {
			params: language ? { language } : {},
			skipAuth: options?.skipAuth === true,
		});

		return {
			id: response.id,
			title: response.title,
			description: response.description,
			category: response.category,
			content: response.content,
			attachmentUrl: response.attachmentUrl,
		};
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '자료 상세를 불러오는데 실패했습니다.');
		}
		throw error;
	}
}

export async function createResourcePost(
	data: CreateResourcePostRequestDto
): Promise<CreateResourcePostResponseDto> {
	try {
		return await apiClient.post<CreateResourcePostResponseDto>(`/resource-post`, data);
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '자료 등록에 실패했습니다.');
		}
		throw error;
	}
}

export async function updateResourcePost(
	resourcePostId: number,
	data: UpdateResourcePostRequestDto
): Promise<UpdateResourcePostResponseDto> {
	try {
		return await apiClient.patch<UpdateResourcePostResponseDto>(
			`/resource-post/${resourcePostId}`,
			data
		);
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '자료 수정에 실패했습니다.');
		}
		throw error;
	}
}

export async function deleteResourcePost(
	resourcePostId: number
): Promise<DeleteResourcePostResponseDto> {
	try {
		return await apiClient.delete<DeleteResourcePostResponseDto>(
			`/resource-post/${resourcePostId}`
		);
	} catch (error) {
		if (error instanceof ApiError) {
			throw new Error(error.message || '자료 삭제에 실패했습니다.');
		}
		throw error;
	}
}
